import { createFileRoute } from "@tanstack/react-router";
import { PeoplePage, Skeleton, usePeopleApi, type AppRole } from "@optymyse/suite-ui";
import { workspaceSlug } from "../lib/hosts";
import { peopleViewer, seesPeople, useWorkspace } from "../lib/workspaces";
import { useMe, meUser } from "../lib/auth";
import { fill, strings } from "../lib/strings";
import { ApiError, askApi } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { Page } from "../components/directory/Page";
import { APP_NAME } from "../../shared/product";

/** Link's one role of its own for its Users: a User holds it or is a Viewer. */
const LINK_ROLES: readonly AppRole[] = [
  { key: "editor", label: "Editor", description: strings.workspaces.people.editor },
];

type PeopleSearch = { tab?: "invitations" };

/**
 * The workspace's People: the suite's page (SuiteUi's PeoplePage), its lists and changes from the People API
 * (usePeopleApi, src/domains/people/routes.ts), asked through the page's own client, which notes the version that
 * answered (askApi). The organisation's owners and admins, and Link's Administrators, choose who has Link and who is its
 * Admin; Administrators make Users Editors; everyone else sees why the page isn't theirs. The API and Impa check every
 * change again.
 */
function PeopleRoute() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);
  const me = useMe();
  const user = meUser(me);
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const viewer = peopleViewer(view.data, user?.id);
  const people = usePeopleApi({ enabled: seesPeople(viewer), fetch: askApi });

  if (view.isLoading || me.isLoading) {
    return (
      <Page wide>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
      </Page>
    );
  }
  if (!view.data || !viewer || view.error instanceof ApiError) return <ToWorkspaces />;
  const { workspace } = view.data;

  return (
    <Page wide>
      <PeoplePage
        app={APP_NAME}
        organisation={{ name: workspace.name, owners: people.owners }}
        viewer={viewer}
        roles={LINK_ROLES}
        noRole="Viewer"
        noRoleDescription={strings.workspaces.people.viewer}
        people={people.people}
        invitations={people.invitations}
        onChange={people.onChange}
        tab={tab ?? "people"}
        onTabChange={(next) => void navigate({ search: next === "invitations" ? { tab: "invitations" } : {} })}
        description={fill(strings.workspaces.people.description, { name: workspace.name })}
      />
    </Page>
  );
}

export const Route = createFileRoute("/admin/settings/people")({
  validateSearch: (search: Record<string, unknown>): PeopleSearch =>
    search.tab === "invitations" ? { tab: "invitations" } : {},
  component: PeopleRoute,
});
